import { usePermissions } from 'hooks';
import every from 'lodash/every';
import partition from 'lodash/partition';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import ITimeRange from 'types/TimeRange';
import { IScheduleReminderDetails } from '../types/types';

export default function useSchedulingSelectionFilter(reminderDetails: IScheduleReminderDetails[]) {
  const { hasPermissionToManage } = usePermissions();

  return useCallback(
    (
      timeRange: ITimeRange<DateTime>,
      allowedMemberIds?: string[] | null,
      allowedProjectIds?: Array<string | null> | null,
      allowedCostCodeIds?: Array<string | null> | null,
      allowedEquipmentIds?: Array<string | null> | null,
      allowBreakReminders?: boolean | null
    ) => {
      const selectedMemberIds = new Set(allowedMemberIds);
      const selectedProjectIds = new Set(allowedProjectIds);
      const selectedCostCodeIds = new Set(allowedCostCodeIds);
      const selectedEquipmentIds = new Set(allowedEquipmentIds);

      const inRangeItems = reminderDetails.filter((reminder) => {
        // If showBreakReminderActions is false we need to filter out breaks because they are not visible
        if (reminder.type === 'break' && allowBreakReminders === false) {
          return false;
        }

        const start = DateTime.fromISO(reminder.startTime).toLocal();
        // Only include things that have the start time on the selected day since we're not showing
        const timeInRange =
          start.toSeconds() >= timeRange.startTime.toSeconds() && start.toSeconds() <= timeRange.endTime.toSeconds();

        if (!timeInRange) {
          return false;
        }

        const reminderProjectId =
          reminder.project && reminder.project.depth > 1 ? reminder.project?.rootProjectId : reminder.project?.id;

        if (allowedProjectIds && !selectedProjectIds.has(reminderProjectId ?? null)) {
          return false;
        }

        const reminderCostCodeId = reminder.costCode?.id ?? null;

        if (allowedCostCodeIds && !selectedCostCodeIds.has(reminderCostCodeId)) {
          return false;
        }

        const reminderEquipmentId = reminder.equipment?.id ?? null;

        if (allowedEquipmentIds && !selectedEquipmentIds.has(reminderEquipmentId)) {
          return false;
        }

        return !allowedMemberIds || reminder.links.some((link) => selectedMemberIds.has(link.memberId));
      });

      // Need to not select events that are not editable by the signed in user.
      return partition(inRangeItems, (item) => {
        return every(item.links, ({ member }) => hasPermissionToManage(member, 'manageSchedule'));
      });
    },
    [hasPermissionToManage, reminderDetails]
  );
}
